import React, { forwardRef, useEffect, useState } from 'react';
import { CustomModal, BuySellDetailsMRT } from '@components';
import {
    Box,
    FormControl,
    InputAdornment,
    InputLabel,
    OutlinedInput,
    Select,
    Typography,
    MenuItem
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { apiBuyContract, apiGetOrder } from '@store';
import { showToast, getMargin } from '@utils';

export const Sell = forwardRef(
    ({ handleClose, selectedContract, user, sym }, ref) => {
        const dispatch = useDispatch();
        const { hedgeMargin } = useSelector((state) => state.hedgeMargin);

        const [state, setState] = useState({
            market: 'PEG',
            type: 'sell',
            price_selected: '',
            seller: user?.CompanyShort,
            buyer: '',
            deliverypoint: 'PEG',
            volume: '',
            exitpoint: '',
            price_percent: '100',
            ordertype: 'internal',
            currency: 'CHF',
            sym: selectedContract?.contract || sym
        });

        const [margin, setMargin] = useState(0);
        const [submitting, setSubmitting] = useState(false);

        useEffect(() => {
            setMargin(
                getMargin({
                    contractName: selectedContract?.contract || sym,
                    hedgeMargin,
                    volume: state.volume
                })
            );
        }, [selectedContract, sym, hedgeMargin, state.volume]);

        useEffect(() => {
            if (user?.CanSellTo?.length > 0) {
                setState((prevState) => ({
                    ...prevState,
                    buyer: user.CanSellTo[0]
                }));
            }
        }, [user]);

        const handleChange = (e) => {
            const { name, value } = e.target;
            setState({
                ...state,
                [name]: value
            });
        };

        const handleSubmit = async (e) => {
            e.preventDefault();
            if (submitting) return;
            setSubmitting(true);
            try {
                const response = await dispatch(apiBuyContract(state));
                if (response.meta.requestStatus === 'fulfilled') {
                    handleClose();
                    showToast('success', 'Order placed successfully');
                    dispatch(apiGetOrder());
                } else if (response.meta.requestStatus === 'rejected') {
                    showToast('error', response.error.message);
                }
            } catch (error) {
                showToast('error', error.message);
            }
            setSubmitting(false);
        };

        return (
            <CustomModal
                handleClose={handleClose}
                modalTitle={`Sell ${
                    selectedContract?.contract || sym
                } - PEG CHF`}
                handleSubmit={handleSubmit}
                buttonText={'Sell'}
                disabled={submitting}
                ref={ref}
            >
                <Box className="">
                    <Box className="flex gap-2 mb-4">
                        <FormControl fullWidth variant="outlined">
                            <InputLabel
                                id="priceLabel"
                                sx={{ bgcolor: '#F5F5F5', p: '0 3px' }}
                            >
                                Price
                            </InputLabel>
                            <OutlinedInput
                                id="price_selected"
                                label="Price"
                                name="price_selected"
                                onChange={handleChange}
                                value={state.price_selected}
                                type="number"
                                inputProps={{ step: 0.01, min: 0 }}
                                required
                                endAdornment={
                                    <InputAdornment
                                        position="start"
                                        sx={{ ml: 2 }}
                                    >
                                        CHF/MWh
                                    </InputAdornment>
                                }
                            />
                        </FormControl>
                        <FormControl fullWidth variant="outlined">
                            <InputLabel
                                id="volumeLabel"
                                sx={{ bgcolor: '#F5F5F5', p: '0 3px' }}
                            >
                                Volume
                            </InputLabel>
                            <OutlinedInput
                                id="volume"
                                label="Volume"
                                name="volume"
                                onChange={handleChange}
                                value={state.volume}
                                type="number"
                                inputProps={{ step: 0.01, min: 0 }}
                                required
                                endAdornment={
                                    <InputAdornment
                                        position="start"
                                        sx={{ ml: 2 }}
                                    >
                                        MW
                                    </InputAdornment>
                                }
                            />
                        </FormControl>
                    </Box>
                    <Box className="flex gap-2 mb-4">
                        <FormControl fullWidth>
                            <InputLabel id="demo-simple-select-label">
                                Buyer
                            </InputLabel>
                            <Select
                                labelId="demo-simple-select-label"
                                id="demo-simple-select"
                                label="Buyer"
                                name="buyer"
                                onChange={handleChange}
                                value={state.buyer}
                                required
                            >
                                {user?.CanSellTo?.map((company) => (
                                    <MenuItem key={company} value={company}>
                                        {company}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <FormControl fullWidth variant="outlined" disabled>
                            <InputLabel
                                id="deliverypointLabel"
                                sx={{ bgcolor: '#F5F5F5', p: '0 3px' }}
                            >
                                Delivery Point
                            </InputLabel>
                            <OutlinedInput
                                id="deliverypoint"
                                label="Delivery Point"
                                name="deliverypoint"
                                value={state.deliverypoint || 'PEG'}
                            />
                        </FormControl>
                    </Box>
                    <Box className="flex gap-2 mb-4">
                        <FormControl fullWidth variant="outlined">
                            <InputLabel
                                id="exitpointLabel"
                                sx={{ bgcolor: '#F5F5F5', p: '0 3px' }}
                            >
                                Exit Point
                            </InputLabel>
                            <OutlinedInput
                                id="exitpoint"
                                label="Exit Point"
                                name="exitpoint"
                                onChange={handleChange}
                                value={state.exitpoint}
                            />
                        </FormControl>
                        <FormControl fullWidth>
                            <InputLabel id="demo-simple-select-label-3">
                                Price %
                            </InputLabel>
                            <Select
                                labelId="demo-simple-select-label-3"
                                id="demo-simple-select-3"
                                label="Price %"
                                name="price_percent"
                                onChange={handleChange}
                                value={state.price_percent}
                                required
                            >
                                <MenuItem value="90">90%</MenuItem>
                                <MenuItem value="100">100%</MenuItem>
                                <MenuItem value="110">110%</MenuItem>
                            </Select>
                        </FormControl>
                    </Box>
                    <BuySellDetailsMRT
                        price={selectedContract?.bid}
                        margin={margin || 0}
                        totalPrice={(
                            parseFloat(selectedContract?.bid) - margin
                        ).toFixed(3)}
                        currency="CHF"
                        deliveryPoint={state.deliverypoint}
                        contractHours={selectedContract?.contract_hours}
                        notionalValue={
                            state?.volume
                                ? (
                                      selectedContract?.contract_hours *
                                      (parseFloat(selectedContract?.bid) -
                                          margin) *
                                      parseFloat(state.volume)
                                  ).toFixed(3)
                                : 0
                        }
                        lastUpdated={selectedContract?.updated_at_cet_str}
                    />
                </Box>
            </CustomModal>
        );
    }
);

export default Sell;
